import React, { useEffect, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import ActivityLoader from '../../Common/ActivityLoader';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';

/** Helpers */
const initialsOf = (fn = '', ln = '') =>
  `${(fn || '').charAt(0)}${(ln || '').charAt(0)}`.toUpperCase();

const colorFromId = (id) => {
  const palette = ['#E3F2FD','#E8F5E9','#FFF3E0','#F3E5F5','#E0F7FA','#FCE4EC','#EDE7F6','#E0F2F1'];
  const idx = Math.abs(parseInt(id, 10) || 0) % palette.length;
  return palette[idx];
};

const isActiveStatus = (status) => {
  const s = String(status ?? '').toLowerCase();
  return s === 'active' || s === '1' || s === 'true';
};

const StatusBadge = ({ status }) => {
  const isActive = isActiveStatus(status);
  return (
    <span style={{
      fontSize: '10px',
      fontWeight: '800',
      padding: '4px 10px',
      borderRadius: '6px',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      backgroundColor: isActive ? '#DCFCE7' : '#F1F5F9',
      color: isActive ? '#166534' : '#475569'
    }}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
};

const SignatureLine = () => (
  <div className="mt-4 mb-2">
    <div style={{ height: '1.5px', background: '#E2E8F0', width: '100%' }} />
    <div className="d-flex justify-content-between text-muted" style={{ fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', marginTop: '4px' }}>
      <span>Signature</span>
      <span>Date</span>
    </div>
  </div>
);

const SignatoryCard = ({ s, selected, onSelect }) => {
  const name = `${s.first_name || ''} ${s.last_name || ''}`.trim();
  const initials = initialsOf(s.first_name, s.last_name);
  const chipBg = colorFromId(s.id);
  
  return (
    <div
      className={`permission-card ${selected ? 'selected' : ''}`}
      onClick={() => onSelect(s)}
      style={{ minHeight: '220px', display: 'flex', flexDirection: 'column' }}
    >
      <div className="d-flex justify-content-between align-items-start w-100 flex-wrap mb-3" style={{ gap: '8px' }}>
        <div className="d-flex flex-grow-1" style={{ minWidth: '0' }}>
            <div className="rounded-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                 style={{ width: 48, height: 48, background: chipBg, color: '#1E293B', fontWeight: 800, fontSize: '14px', border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                {initials || '??'}
            </div>
            <div style={{ minWidth: '0' }}>
                <span className="text-truncate" style={{ display: 'block', fontWeight: '700', fontSize: '14px', color: selected ? '#4F46E5' : '#1E293B' }}>
                    {name || '—'}
                </span>
                <small className="text-muted d-block text-truncate mt-1" style={{ fontWeight: '500', letterSpacing: '0.02em' }}>
                    {s.designation || '—'}
                </small>
            </div>
        </div>
        <div className="flex-shrink-0">
            <StatusBadge status={s.status} />
        </div>
      </div>

      <div className="bg-light p-2 rounded mb-3" style={{ fontSize: '11px', border: '1px dashed #E2E8F0' }}>
          <div className="text-muted mb-1"><i className="fa-solid fa-user-plus mr-1" /> {s.created_by || 'System'}</div>
          <div className="text-muted"><i className="fa-solid fa-clock-rotate-left mr-1" /> {s.updated_at || 'Never'}</div>
      </div>

      <div className="mt-auto">
        <SignatureLine />
      </div>
      
      {selected && (
        <div className="text-right mt-2" style={{ color: '#4F46E5', fontSize: '11px', fontWeight: '700' }}>
            <i className="fa-solid fa-circle-check mr-1" /> Selected for Management
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ onNew }) => (
  <div className="text-center p-5 border-0 rounded-lg bg-white shadow-sm mt-3 mx-2">
    <div className="mb-3">
      <span className="rounded-circle d-inline-flex align-items-center justify-content-center" 
            style={{ width: 72, height: 72, background: '#F5F7FF', color: '#4F46E5' }}>
        <i className="fa-solid fa-signature fa-2x" />
      </span>
    </div>
    <h5 className="font-weight-bold" style={{ color: '#1E293B' }}>No signatories yet</h5>
    <p className="text-muted mx-auto mb-4" style={{ maxWidth: '300px' }}>
      Define the authorized personnel who will sign printed reports and financial statements.
    </p>
    <button className="btn btn-modern-primary px-4" onClick={onNew}>
      <i className="fa-solid fa-plus mr-2" /> New Signatory
    </button>
  </div>
);

const ReportSignature = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const { getSignatories, signatories, dispatch, loadingSignatories, errorSignatories, showDeleteSignatoryAlert } = useConfig();

  useEffect(() => { getSignatories(); }, []); // eslint-disable-line

  const handleNew = () => openSettings('New Signatory', 'ConfigNewSignatory', null, "Save Signatory");
  const handleDelete = () => activeRecord && showDeleteSignatoryAlert(activeRecord);
  const handleEdit = () => activeRecord && openSettings('Edit Signatory', 'ConfigEditSignatory', activeRecord);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleNew}>
        <i className="dropdown-icon fa-regular fa-plus"></i>
        <span>New Signatory</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleEdit} disabled={!activeRecord}>
        <i className="dropdown-icon fa-regular fa-edit"></i>
        <span>Edit Signatory</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={() => getSignatories(true)}>
        <i className="dropdown-icon fa fa-sync"></i>
        <span>Refresh</span>
      </button>
      <div className="dropdown-divider"></div>
      <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={handleDelete}>
        <i className="dropdown-icon fa fa-trash-alt text-danger" /> <span>Remove Signatory</span>
      </button>
    </>
  );

  const list = Array.isArray(signatories) ? signatories : [];
  const activeList = list.filter((s) => isActiveStatus(s.status));

  return (
    <SettingContainer title="Report Signatories" icon="fa fa-signature" Menu={menu}>
      <div className="overflow-auto" style={{ maxHeight: '55vh' }}>
        {errorSignatories && <Notification type="danger" message={String(errorSignatories)} />}

        {loadingSignatories && (
          <div className="py-4 text-center">
            <ActivityLoader />
          </div>
        )}

        {!loadingSignatories && list.length === 0 && <EmptyState onNew={handleNew} />}

        {!loadingSignatories && list.length > 0 && (
          <div className="row">
            {list.map((s) => (
              <div className="col-lg-4 col-md-6 mb-3" key={s.id}>
                <SignatoryCard
                  s={s}
                  selected={activeRecord?.id === s.id}
                  onSelect={(rec) => setActiveRecord(rec)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Print-friendly block */}
        {!loadingSignatories && list.length > 0 && (
          <div className="mt-5 px-3">
            <div className="d-flex align-items-center mb-3">
                <div className="flex-grow-1" style={{ height: '1px', background: '#E2E8F0' }} />
                <span className="mx-3 text-muted" style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                    Print Layout Preview
                </span>
                <div className="flex-grow-1" style={{ height: '1px', background: '#E2E8F0' }} />
            </div>

            <div className="row">
              {activeList.map((s) => (
                <div className="col-md-6 mb-4" key={`print-${s.id}`}>
                  <div className="p-4 bg-white border rounded shadow-sm" style={{ borderStyle: 'solid !important', borderColor: '#F1F5F9 !important' }}>
                    <div className="font-weight-bold text-dark h6 mb-1">{`${s.first_name} ${s.last_name}`}</div>
                    <div className="text-muted small font-italic" style={{ borderBottom: '2px solid #F1F5F9', paddingBottom: '12px' }}>{s.designation}</div>
                    <div className="mt-5" style={{ height: '1px', background: '#334155' }} />
                    <div className="d-flex justify-content-between text-dark pt-1" style={{ fontSize: '10px', fontWeight: '800' }}>
                      <span>AUTHORIZED SIGNATURE</span>
                      <span>DATE</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SettingContainer>
  );
};

export default ReportSignature;
